import React, { Component } from "react";
import { Map, TileLayer, Marker, Tooltip, Polyline, ZoomControl } from "react-leaflet";
import L from "leaflet";

export class MapSummary extends Component {
    constructor(props) {
        super(props);
        this.mapRef = React.createRef();
        this.state = {
            mapReady: false,
            hoveredPath: null,
            hoveredPoint: null,
            showShapeWarning: false
        };
    }

    componentDidMount() {
        setTimeout(() => {
            this.setState({ mapReady: true });
            this.fitMapToBounds();
        }, 100);
    }

    componentDidUpdate(prevProps) {
        if (prevProps.points !== this.props.points || prevProps.paths !== this.props.paths) {
            this.fitMapToBounds();
        }
    }

    createCircleMarker(size = 16, isStop = false) {
        const className = `custom-marker ${isStop ? "stop" : "nonstop"}`;

        return new L.DivIcon({
            html: `<div class="${className}" style="width:${size}px;height:${size}px;">
                    ${isStop ? "●" : ""}
                </div>`,
            className: "",
            iconSize: [size, size],
            iconAnchor: [size / 2, size / 2],
            popupAnchor: [0, -size / 2],
        });
    }

    fitMapToBounds() {
        const { points, paths } = this.props;
        const map = this.mapRef.current && this.mapRef.current.leafletElement;

        if (!map || !this.state.mapReady) {
            return;
        } 

        const bounds = this.calculateBounds(points, paths);

        if (bounds.length > 0) {
            const latLngBounds = L.latLngBounds(bounds);
            map.fitBounds(latLngBounds, {
                padding: [50, 50],
                maxZoom: 16
            });
        }
    }

    darkenColor(hex, amount = 20) {
        let col = hex.replace("#", "");
        let num = parseInt(col, 16);
        let r = Math.max(0, (num >> 16) - amount);
        let g = Math.max(0, ((num >> 8) & 0x00FF) - amount);
        let b = Math.max(0, (num & 0x0000FF) - amount);
        return "#" + ((r << 16) | (g << 8) | b).toString(16).padStart(6, "0");
    }

    calculateBounds(points, paths) {
        const bounds = [];

        if (undefined !== points && points.length > 0) {
            points.forEach((pt) => {
                if (this.isValidCoordinate(pt.latitude, pt.longitude)) {
                    bounds.push([pt.latitude, pt.longitude]);
                }
            });
        }

        if (undefined !== paths && paths.length > 0) {
            paths.forEach((path) => {
                if (path.shape && path.shape.segments && path.shape.segments.length > 0) {
                    path.shape.segments[0].breakpoints
                        .filter(bp => this.isValidCoordinate(bp.latitude, bp.longitude))
                        .forEach(bp => {
                            bounds.push([bp.latitude, bp.longitude]);
                        });
                }
            });
        }

        return bounds;
    }

    isValidCoordinate(lat, lng) {
        return (
            typeof lat === 'number' &&
            typeof lng === 'number' &&
            !isNaN(lat) &&
            !isNaN(lng) &&
            lat >= -90 && lat <= 90 &&
            lng >= -180 && lng <= 180
        );
    }

    handlePathMouseOver(pathId) {
        this.setState({ hoveredPath: pathId });
    }

    handlePathMouseOut(){
        this.setState({ hoveredPath: null });
    }

    renderPoints() {
        const { points } = this.props;

        if(undefined === points || points.length === 0) {
            return null;
        }

        return points.map((point) => {
            if (!this.isValidCoordinate(point.latitude, point.longitude)) {
                return null;
            }

            const tooltipText = this.getPointTooltipText(point);

            const isHovered = this.state.hoveredPoint === point.id;
            const markerSize = point.isStop ? (isHovered ? 26 : 18) : (isHovered ? 20 : 14);

            return (
                <Marker
                    key={point.id || `point-${point.latitude}-${point.longitude}`}
                    position={[point.latitude, point.longitude]}
                    icon={this.createCircleMarker(markerSize, point.isStop)}
                    onMouseOver={() => this.setState({ hoveredPoint: point.id })}
                    onMouseOut={() => this.setState({ hoveredPoint: null })}
                >
                    {this.state.hoveredPoint === point.id && tooltipText && (
                        <Tooltip
                            direction="top"
                            offset={[0, -10]}
                            opacity={0.9}
                            className="custom-tooltip"
                        >
                            <div style={{ fontSize: "12px", fontWeight: "bold" }}>
                                {tooltipText}
                            </div>
                        </Tooltip>
                    )}
                </Marker>
            );
        }).filter(Boolean);
    }

    getPointTooltipText(point) {
        if (point.isStop && point.code && point.name) {
            return `${point.code} - ${point.name}`;
        }

        return point.name;
    }

    renderPaths() {
        const { paths, points } = this.props;

        if (undefined === paths || paths.length === 0) {
            return null;
        }

        return paths.map((path) => {
            const pathPositions = this.getPathPositions(path, points);

            if (undefined === pathPositions || pathPositions.length === 0) {
                return null;
            }

            const isHovered = this.state.hoveredPath === path.id;
            const pathColor = path.backColor;
            const hoverColor = this.darkenColor(pathColor, 30);

            return (
                <Polyline
                    key={path.id || `path-${Math.random()}`}
                    positions={pathPositions}
                    color={isHovered ? hoverColor : pathColor}
                    weight={isHovered ? 8 : 5}
                    opacity={0.8}
                    onMouseOver={() => this.handlePathMouseOver(path.id)}
                    onMouseOut={() => this.handlePathMouseOut()}
                >
                    <Tooltip
                        direction="center"
                        sticky
                        opacity={0.9}
                        className="custom-tooltip"
                    >
                        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            {path.code || path.name || `Route ${path.id}`}
                        </div>
                    </Tooltip>
                </Polyline>
            );
        }).filter(Boolean);
    }

    getPathPositions(path) {
        if (path.shape && path.shape.segments && path.shape.segments.length > 0) {
            return path.shape.segments[0].breakpoints
                .filter(bp => this.isValidCoordinate(bp.latitude, bp.longitude))
                .map(bp => [bp.latitude, bp.longitude]);
        }

        return [];
    }

    render() {
        const { points, paths } = this.props;
        const hasData = (undefined !== points && points.length > 0) || (undefined !== paths && paths.length > 0);

        return (
            <div className="schematicmap-summary-container">
                <div className="schematicmap-summary-map-container">
                    {!hasData ? (
                        <div className="schematicmap-no-data">
                            <p>Ocorreu um erro a apresentar o mapa!</p>
                        </div>
                    ) : (
                        <Map
                            ref={this.mapRef}
                            center={[40.2, -8.4]}
                            zoom={13}
                            style={{ height: "100%", width: "100%" }}
                            zoomControl={false}
                        >
                            <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                maxZoom={18}
                                minZoom={3}
                            />

                            <ZoomControl position="bottomright" />

                            {this.renderPoints()}
                            {this.renderPaths()}
                        </Map>
                    )}
                </div>

                <div className="schematicmap-legend">
                    <div className="schematicmap-legend-item">
                        <div className="schematicmap-legend-color stop"></div>
                        <span>Paragens</span>
                    </div>
                    <div className="schematicmap-legend-item">
                        <div className="schematicmap-legend-color nonstop"></div>
                        <span>Pontos Notáveis</span>
                    </div>
                    {this.props.showShapeWarning && (
                        <div className="schematicmap-legend-item">
                            <div className="schematicmap-legend-color warning"></div>
                            <span>Percursos sem shape não serão exibidos neste mapa.</span>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}